import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Brain, Sparkles, ChevronRight, Play, Square } from "lucide-react";
import api from "../lib/api.js";
import { speakWithPersona, stopSpeaking } from "../lib/voice.js";

/**
 * Surfaces today's proactive Coach nudge on the Home dashboard.
 * Pulls /coach/history first for an existing nudge; otherwise calls /coach/nudge once.
 * Falls back to `fallback` copy if Coach is unavailable.
 *
 * On the first home visit of each day, attempts to auto-play the nudge in the
 * user's selected Coach persona voice. Auto-play is only attempted once per day
 * and skipped if the user has already played it.
 */
export default function CoachNudgeCard({ fallback }) {
  const [nudge, setNudge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const triedAutoplayRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);
    api.get("/coach/history?limit=20").then((r) => {
      if (cancelled) return;
      const existing = r.data.find(
        (m) =>
          m.role === "assistant" &&
          m.kind === "nudge" &&
          (m.created_at || "").startsWith(today)
      );
      if (existing) {
        setNudge(existing.text);
        setLoading(false);
      } else {
        api.post("/coach/nudge").then((nr) => {
          if (!cancelled) setNudge(nr.data.text);
        }).catch(() => {}).finally(() => !cancelled && setLoading(false));
      }
    }).catch(() => !cancelled && setLoading(false));
    return () => { cancelled = true; stopSpeaking(); };
  }, []);

  // Attempt auto-play once per day on first home visit.
  // Browsers block audio until a user gesture, so we register a one-time
  // pointer/key listener as a fallback. We only mark "played" in localStorage
  // when the audio actually starts (via onStart), so a blocked attempt won't
  // suppress the gesture-fallback.
  useEffect(() => {
    if (!nudge || triedAutoplayRef.current) return;
    const today = new Date().toISOString().slice(0, 10);
    const key = `ps_nudge_audio_${today}`;
    if (localStorage.getItem(key) === "1") return;
    triedAutoplayRef.current = true;

    let started = false;
    let inflight = false;
    const cleanupListeners = () => {
      window.removeEventListener("pointerdown", onGesture, true);
      window.removeEventListener("keydown", onGesture, true);
      window.removeEventListener("touchstart", onGesture, true);
    };

    const tryPlay = async () => {
      if (started || inflight) return;
      inflight = true;
      const base = process.env.REACT_APP_BACKEND_URL || "";
      await speakWithPersona(nudge, {
        apiBase: base,
        onStart: () => {
          started = true;
          setPlaying(true);
          localStorage.setItem(key, "1");
          cleanupListeners();
        },
        onEnd: () => { setPlaying(false); inflight = false; },
      });
      inflight = false;
    };

    function onGesture() { tryPlay(); }

    // Try immediately (might be blocked depending on browser/policy)
    const immediate = setTimeout(tryPlay, 600);

    // Always register gesture fallback so a blocked attempt still gets caught
    window.addEventListener("pointerdown", onGesture, true);
    window.addEventListener("keydown", onGesture, true);
    window.addEventListener("touchstart", onGesture, true);

    return () => {
      clearTimeout(immediate);
      cleanupListeners();
    };
  }, [nudge]);

  const togglePlay = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (playing) {
      stopSpeaking();
      setPlaying(false);
      return;
    }
    if (!nudge) return;
    setPlaying(true);
    const base = process.env.REACT_APP_BACKEND_URL || "";
    await speakWithPersona(nudge, {
      apiBase: base,
      onEnd: () => setPlaying(false),
    });
  };

  const display = nudge || fallback;
  const snippet =
    display && display.length > 200 ? display.slice(0, 195).trimEnd() + "…" : display;

  return (
    <Link
      to="/coach"
      className="block mt-5 ps-fade-up"
      data-testid="ai-coach-card"
      style={{ animationDelay: "0.35s" }}
    >
      <div className="ps-card p-5 flex gap-4 items-start relative overflow-hidden">
        <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-[#a78bfa]/20 blur-3xl" />
        <div
          className="relative w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{
            background: "linear-gradient(160deg, #a78bfa55 0%, #a78bfa22 40%, #a78bfa10 100%)",
            border: "1px solid #a78bfa66",
            boxShadow:
              "inset 0 1.5px 0 rgba(255,255,255,0.32), inset 0 -3px 6px rgba(0,0,0,0.45), 0 4px 14px -4px #a78bfa77",
          }}
        >
          <Brain size={22} color="#a78bfa" strokeWidth={2.4} />
        </div>
        <div className="relative flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#a78bfa] font-bold flex items-center gap-1.5">
            <Sparkles size={11} /> {nudge ? "Today's coach nudge" : "AI Wellness Coach"}
          </div>
          <div className="font-semibold mt-1 ps-serif text-[14px] leading-snug" data-testid="home-coach-nudge">
            {loading ? "Generating today's nudge…" : snippet}
          </div>
          {nudge && (
            <div className="mt-2 text-[10px] uppercase tracking-widest text-[#4ade80] font-bold inline-flex items-center gap-0.5">
              Open chat <ChevronRight size={11} />
            </div>
          )}
        </div>
        {/* Play / Stop persona-voice button */}
        {nudge && (
          <button
            onClick={togglePlay}
            data-testid="home-nudge-play"
            aria-label={playing ? "Stop coach voice" : "Play coach voice"}
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all"
            style={{
              background: playing
                ? "linear-gradient(160deg, #ef4444aa 0%, #ef444466 100%)"
                : "linear-gradient(160deg, #4ade8088 0%, #4ade8033 100%)",
              border: `1px solid ${playing ? "#ef444499" : "#4ade8099"}`,
              boxShadow: playing
                ? "inset 0 1.5px 0 rgba(255,255,255,0.3), 0 6px 18px -6px #ef4444aa"
                : "inset 0 1.5px 0 rgba(255,255,255,0.3), 0 6px 18px -6px #4ade80aa",
            }}
          >
            {playing ? (
              <Square size={14} color="#fff" fill="#fff" />
            ) : (
              <Play size={14} color="#022014" fill="#022014" />
            )}
          </button>
        )}
      </div>
    </Link>
  );
}

